// frontend/src/pages/ScorePage.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Header from '../components/Header';
import BPMInput from '../components/BPM';
import ScoreSheet from '../components/ScoreSheet';
import SaveButton from '../components/SaveButton';
import AddPartButton from '../components/AddPartButton';
import AdMaxUnit from '../components/AdMaxUnit';
import '../styles/common/Spinner.css';
import '../styles/ScorePage.css';
import { Circleof5thPanel as CircleOfFifths } from '../components/Circleof5thPanel';
import KeySelectModal from '../components/KeySelectModal';
import Modal from '../components/common/Modal';
import Footer from '../components/Footer';
import '../styles/Layout.css';
import { useChordAudio } from '../hooks/useChordAudio';
import { getStoredUserProfile } from '../utils/authStorage';
import { isAdEnabled } from '../utils/adVisibility';
import { buildApiUrl } from '../lib/api';
import { DEFAULT_SCORE_ARTIST, DEFAULT_SCORE_TITLE } from '../utils/scoreDefaults';
import { resolveUserIdentity } from '../utils/userIdentity';
import useUserPlan from '../hooks/useUserPlan';
import useLocale from '../hooks/useLocale';
import { setLocalePreference } from '../utils/locale';
import { DIATONIC_CHORDS } from '../data/diatonicChords';

const DRAFT_STORAGE_KEY = 'chord_analyzer_draft_score';
const DEFAULT_DRAFT_SCORE = {
  title: '',
  artist: '',
  tempo: 120,
  parts: [
    {
      key: 'C',
      rows: [
        {
          label: '',
          chords: Array(16).fill(''),
        },
      ],
    },
  ],
};

const PALETTE_GROUPS = [
  { name: 'Diatonic', tone: 'blue', chords: ['Cm7', 'Dm7b5', 'EbM7', 'Fm7', 'Gm7', 'AbM7', 'Bb7'] },
  { name: 'Parallel', tone: 'green', chords: ['CM7', 'Dm7', 'Em7', 'FM7', 'G7', 'Am7', 'Bm7b5'] },
  { name: 'Subst. Dom', tone: 'amber', chords: ['C#7', 'Db7'] },
  { name: 'Backdoor', tone: 'purple', chords: ['Bm7b5'] },
  { name: 'Blackadder', tone: 'violet', chords: ['Gaug/Db'] },
  { name: '2nd. Dom', tone: 'red', chords: ['C7', 'D7', 'Eb7', 'F7', 'Ab7'] },
];

const NOTE_TO_SEMITONE = { C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11 };
const SHARP_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_NOTES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

function keyRoot(key) {
  return String(key || '').replace(/m$/, '');
}

function transposeChordSymbol(chord, fromKey, toKey) {
  const from = NOTE_TO_SEMITONE[keyRoot(fromKey)];
  const to = NOTE_TO_SEMITONE[keyRoot(toKey)];
  if (!chord || from === undefined || to === undefined) return chord;
  const interval = (to - from + 12) % 12;
  return chord.replace(/(^|\/)([A-G](?:#|b)?)/g, (match, prefix, note) => {
    const semitone = NOTE_TO_SEMITONE[note];
    const names = /b/.test(note) || /b/.test(keyRoot(toKey)) ? FLAT_NOTES : SHARP_NOTES;
    return semitone === undefined ? match : `${prefix}${names[(semitone + interval) % 12]}`;
  });
}

function ChordPalette({ onApply, selectedKey, locale }) {
  const paletteGroups = PALETTE_GROUPS.map((group) => (
    group.name === 'Diatonic'
      ? { ...group, chords: Object.values(DIATONIC_CHORDS[selectedKey] || {}).length ? Object.values(DIATONIC_CHORDS[selectedKey]) : group.chords }
      : {
        ...group,
        chords: group.chords.map((chord) => transposeChordSymbol(chord, 'Cm', selectedKey)),
      }
  ));
  return (
    <section className="chord-palette" aria-label="Chord palette">
      <div className="palette-heading">
        <span>CHORD PALETTE</span>
        <small>{locale === 'ja' ? 'クリックで選択中のセルへ入力' : 'Click to enter in the selected cell'}</small>
      </div>
      <div className="palette-grid">
        {paletteGroups.map((group) => (
          <div className="palette-group" key={group.name}>
            <span className={`palette-label ${group.tone}`}>{group.name}</span>
            <div className="palette-chips">
              {group.chords.map((chord) => (
                <button type="button" key={chord} onClick={() => onApply(chord)}>{chord}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function loadDraftScore() {
  if (typeof window === 'undefined') {
    return DEFAULT_DRAFT_SCORE;
  }

  try {
    const stored = window.sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!stored) {
      return DEFAULT_DRAFT_SCORE;
    }
    const parsed = JSON.parse(stored);
    return {
      title: parsed.title || '',
      artist: parsed.artist || '',
      tempo: parsed.tempo || 120,
      parts: Array.isArray(parsed.parts) && parsed.parts.length
        ? parsed.parts
        : DEFAULT_DRAFT_SCORE.parts,
    };
  } catch (error) {
    console.error('Failed to parse local draft score', error);
    return DEFAULT_DRAFT_SCORE;
  }
}

export default function ScorePage({ user: userProp, scoreId: scoreIdProp }) {
  const fallbackUser = useMemo(() => {
    return getStoredUserProfile();
  }, []);
  const user = userProp || fallbackUser;
  const locale = useLocale();
  const fallbackScoreId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('scoreid');
  }, []);
  const [activeScoreId, setActiveScoreId] = useState(() => scoreIdProp || fallbackScoreId);
  const initialDraft = useMemo(() => loadDraftScore(), []);
  const [title, setTitle] = useState(() => (activeScoreId ? '' : initialDraft.title));
  const [artist, setArtist] = useState(() => (activeScoreId ? '' : initialDraft.artist));
  const [loading, setLoading] = useState(true);
  const [isScoreNotFound, setIsScoreNotFound] = useState(false);
  const [parts, setParts] = useState(() => (activeScoreId ? DEFAULT_DRAFT_SCORE.parts : initialDraft.parts));
  const [tempo, setTempo] = useState(() => (activeScoreId ? 120 : initialDraft.tempo));
  const [activeKeyPartIndex, setActiveKeyPartIndex] = useState(null);
  const [activeRowKeyTarget, setActiveRowKeyTarget] = useState(null);
  const [harmonyPreviewKey, setHarmonyPreviewKey] = useState(null);
  const [pendingKeyChange, setPendingKeyChange] = useState(null);
  const [isHarmonyToolsVisible, setIsHarmonyToolsVisible] = useState(true);
  const initialized = useRef(false);
  const isDraftMode = !activeScoreId;
  const identity = resolveUserIdentity(user);
  const userId = identity.userId;
  const { isPro, isLoading: isLoadingPlan } = useUserPlan(user);
  const isGuestDraft = !userId && isDraftMode;
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingArtist, setIsEditingArtist] = useState(false);

  // 自動保存用ステート
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const autoSaveTimer = useRef(null);
  const lastSavedScoreRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSequencePlaying, setIsSequencePlaying] = useState(false);
  const [sequenceProgress, setSequenceProgress] = useState({ current: 0, total: 0 });
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [libraryScores, setLibraryScores] = useState([]);
  const [libraryStatus, setLibraryStatus] = useState('idle');
  const [expandedLibraryArtist, setExpandedLibraryArtist] = useState(null);
  const [activeCell, setActiveCell] = useState(null);
  const [activeInputCell, setActiveInputCell] = useState(null);
  const [playbackStartCell, setPlaybackStartCell] = useState(null);
  const sequenceRef = useRef({ timeoutId: null, queue: [], index: 0 });
  const { playChord, stop: stopChord } = useChordAudio({
    initialVolume: 0.6,
    instrument: 'synth',
  });
  const beatDurationMs = useMemo(() => {
    const safeTempo = Math.max(1, tempo || 120);
    return (60 / safeTempo) * 1000;
  }, [tempo]);
  const canShowAds =
    !loading &&
    isAdEnabled() &&
    !isScoreNotFound &&
    !isPro &&
    (!userId || !isLoadingPlan);

  const libraryArtists = useMemo(() => {
    const grouped = new Map();
    libraryScores.forEach((score) => {
      const artistName = (typeof score.artist === 'string' ? score.artist.trim() : '') || DEFAULT_SCORE_ARTIST;
      const entries = grouped.get(artistName) || [];
      entries.push(score);
      grouped.set(artistName, entries);
    });
    return [...grouped.entries()].sort(([left], [right]) => left.localeCompare(right, 'ja'));
  }, [libraryScores]);

  useEffect(() => {
    if (!isLibraryOpen || !userId) return;
    let cancelled = false;
    setLibraryStatus('loading');
    fetch(`${buildApiUrl('/get-scores')}?userId=${encodeURIComponent(userId)}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Failed to load library')))
      .then((data) => {
        if (cancelled) return;
        setLibraryScores(Array.isArray(data.scores) ? data.scores : (Array.isArray(data) ? data : []));
        setLibraryStatus('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load score library', error);
        setLibraryStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [isLibraryOpen, userId]);

  useEffect(() => {
    if (!userId || !activeScoreId || initialized.current) {
      setLoading(false);
      return;
    }

    fetch(
      `${buildApiUrl('/get-scores')}?userId=${encodeURIComponent(
        userId
      )}`
    )
      .then((res) => res.json())
      .then((data) => {
        const scores = data.scores || data || [];
        const matched = scores.find((score) => score.scoreId === activeScoreId);
        if (matched) {
          setIsScoreNotFound(false);
          if (title !== (matched.title || '')) {
            setTitle(matched.title || '');
          }
          const scorePayload = matched.score;
          if (scorePayload?.parts) {
            setParts(scorePayload.parts);
          }
          const fetchedTempoSource = (scorePayload && typeof scorePayload === 'object')
            ? scorePayload.tempo
            : matched.tempo;
          const fetchedTempo = Number(fetchedTempoSource);
          if (!Number.isNaN(fetchedTempo) && fetchedTempo > 0) {
            setTempo(fetchedTempo);
          }
          setArtist(matched.artist || '');
          initialized.current = true;
        } else {
          setIsScoreNotFound(true);
          initialized.current = true;
        }
      })
      .catch((err) => console.error('Failed to fetch score title', err))
      .finally(() => setLoading(false));
  }, [activeScoreId, title, user, userId]);

  useEffect(() => {
    if (!isDraftMode || typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
      title,
      artist,
      tempo,
      parts,
    }));
  }, [artist, isDraftMode, parts, tempo, title]);

  // SaveButton と同じフォーマットで自動保存する関数
  const autoSaveScore = React.useCallback(async () => {
    if (!userId || !activeScoreId) return;
    if (!isDirty) return;

    const normalizedTitle = (typeof title === 'string' ? title.trim() : '') || DEFAULT_SCORE_TITLE;
    const normalizedArtist = typeof artist === 'string' ? artist.trim() : '';
    const normalizedParts = Array.isArray(parts) ? parts : [];

    const payload = {
      userId,
      email: identity.email,
      scoreId: activeScoreId,
      title: normalizedTitle,
      artist: normalizedArtist,
      score: {
        updatedAt: new Date().toISOString(),
        tempo: tempo || 120,
        parts: normalizedParts.map((part, index) => ({
          name: String(index + 1),
          key: part.key || 'C',
          rows: Array.isArray(part.rows) ? part.rows : [],
        })),
      },
    };

    try {
      const response = await fetch(
        buildApiUrl('/save-score'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error('Auto-save failed');
      }

      // レスポンスの中身は特に使わないが、JSON パースはしておく
      await response.json().catch(() => null);

      const serialized = JSON.stringify(payload);
      lastSavedScoreRef.current = serialized;
      const savedAt = new Date();
      setLastSavedAt(savedAt);
      setSaveStatus('saved');
      setIsDirty(false);
    } catch (error) {
      console.error('Auto-save error:', error);
      setSaveStatus('error');
    }
  }, [activeScoreId, artist, identity.email, isDirty, parts, tempo, title, userId]);

  // parts/title の変更をデバウンスして自動保存
  useEffect(() => {
    if (loading) return;
    if (!userId || !activeScoreId) return;

    // 未変更なら何もしない（勝手に保存を走らせない）
    if (!isDirty) {
      return;
    }

    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    // 変更があったので「自動保存中」ステータスを表示（実際の保存はディレイ後）
    setSaveStatus('saving');

    autoSaveTimer.current = setTimeout(() => {
      autoSaveScore();
    }, 1500); // 1.5秒入力が止まったら保存

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [activeScoreId, autoSaveScore, isDirty, loading, parts, title, userId]);

  const handleBaseKeyChange = (newKey) => {
    setParts((prev) => prev.map((part) => ({ ...part, key: newKey })));
    setHarmonyPreviewKey(newKey);
    setIsDirty(true);
  };

  const applyKeyChange = (change, transposeChords) => {
    if (change.type === 'row' || change.type === 'row-reset') {
      setParts((prev) => prev.map((part, partIndex) => (
        partIndex !== change.partIndex ? part : {
          ...part,
          rows: part.rows.map((row, rowIndex) => (
            rowIndex !== change.rowIndex ? row : (() => {
              const nextRow = {
                ...row,
                chords: transposeChords
                  ? row.chords.map((chord) => transposeChordSymbol(chord, change.fromKey, change.toKey))
                  : row.chords,
              };
              if (change.type === 'row-reset') {
                delete nextRow.key;
              } else {
                nextRow.key = change.toKey;
              }
              return nextRow;
            })()
          )),
        }
      )));
    } else {
      setParts((prev) => prev.map((part) => ({
        ...part,
        key: change.toKey,
        rows: part.rows.map((row) => (
          row.key || !transposeChords ? row : {
            ...row,
            chords: row.chords.map((chord) => transposeChordSymbol(chord, change.fromKey, change.toKey)),
          }
        )),
      })));
    }
    setHarmonyPreviewKey(change.toKey);
    setIsDirty(true);
    setPendingKeyChange(null);
  };

  const requestRowKeyReset = (partIndex, rowIndex) => {
    const part = parts[partIndex];
    const row = part?.rows?.[rowIndex];
    if (!row?.key) return;
    setPendingKeyChange({
      type: 'row-reset',
      partIndex,
      rowIndex,
      fromKey: row.key,
      toKey: part.key || 'C',
    });
  };

  const handleTempoChange = (newTempo) => {
    setTempo(newTempo);
    setIsDirty(true);
  };

  const handleRowsChange = (partIndex, newRows) => {
    setParts((prev) =>
      prev.map((part, i) =>
        i === partIndex ? { ...part, rows: newRows } : part
      )
    );
    setIsDirty(true);
  };

  const applyPaletteChord = useCallback((chord) => {
    if (!activeInputCell) return;
    const { partIndex, rowIndex, colIndex } = activeInputCell;
    setParts((prev) => prev.map((part, index) => {
      if (index !== partIndex) return part;
      const rows = (part.rows || []).map((row, rowIndexValue) => {
        if (rowIndexValue !== rowIndex) return row;
        const chords = [...(row.chords || [])];
        chords[colIndex] = chord;
        return { ...row, chords };
      });
      return { ...part, rows };
    }));
    setIsDirty(true);
  }, [activeInputCell]);

  const removePart = (index) => {
    if (parts.length <= 1) return;
    setParts((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const addPart = () => {
    setParts((prev) => [
      ...prev,
      { key: prev[0]?.key || 'C', rows: [{ label: '', lyrics: Array(16).fill(''), chords: Array(16).fill('') }] },
    ]);
    setIsDirty(true);
  };

  const stopSequence = useCallback(() => {
    const { timeoutId } = sequenceRef.current;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    sequenceRef.current = { timeoutId: null, queue: [], index: 0 };
    setIsSequencePlaying(false);
    setSequenceProgress({ current: 0, total: 0 });
    setActiveCell(null);
    stopChord();
  }, [stopChord]);

  const runSequenceStep = useCallback(() => {
    const { queue, index } = sequenceRef.current;
    if (index >= queue.length) {
      stopSequence();
      return;
    }
    const item = queue[index];
    setSequenceProgress({ current: index + 1, total: queue.length });
    setActiveCell({
      partIndex: item.partIndex,
      rowIndex: item.rowIndex,
      colIndex: item.colIndex,
    });
    if (item.chord && item.chord.trim()) {
      playChord(item.chord);
    }
    sequenceRef.current.index += 1;
    sequenceRef.current.timeoutId = setTimeout(runSequenceStep, beatDurationMs);
  }, [beatDurationMs, playChord, stopSequence]);

  const startSequence = useCallback(() => {
    if (!parts.length) return;
    const queue = [];
    parts.forEach((part, partIndex) => {
      (part.rows || []).forEach((row, rowIndex) => {
        (row.chords || []).forEach((chord, colIndex) => {
          if (chord?.trim()) queue.push({ partIndex, rowIndex, colIndex, chord });
        });
      });
    });
    if (!queue.length) return;
    const startIndex = playbackStartCell
      ? queue.findIndex(
        (cell) =>
          cell.partIndex === playbackStartCell.partIndex
          && cell.rowIndex === playbackStartCell.rowIndex
          && cell.colIndex === playbackStartCell.colIndex
      )
      : -1;
    const orderedQueue =
      startIndex > 0
        ? [...queue.slice(startIndex), ...queue.slice(0, startIndex)]
        : queue;
    sequenceRef.current = { timeoutId: null, queue: orderedQueue, index: 0 };
    setSequenceProgress({ current: 0, total: orderedQueue.length });
    setIsSequencePlaying(true);
    runSequenceStep();
  }, [parts, playbackStartCell, runSequenceStep]);

  useEffect(() => {
    return () => {
      stopSequence();
    };
  }, [stopSequence]);

  const toggleSequence = () => {
    if (isSequencePlaying) {
      stopSequence();
    } else {
      startSequence();
    }
  };

  const handleActiveInputCellChange = useCallback((partIndex, cell) => {
    if (!cell) return;
    const part = parts[partIndex];
    const row = part?.rows?.[cell[0]];
    setHarmonyPreviewKey(row?.key || part?.key || null);
    setActiveInputCell({ partIndex, rowIndex: cell[0], colIndex: cell[1] });
    setPlaybackStartCell((prev) => {
      const next = {
        partIndex,
        rowIndex: cell[0],
        colIndex: cell[1],
      };
      if (
        prev
        && prev.partIndex === next.partIndex
        && prev.rowIndex === next.rowIndex
        && prev.colIndex === next.colIndex
      ) {
        return prev;
      }
      return next;
    });
  }, [parts]);

  const submitTitleEdit = () => {
    setTitle((prev) => prev.trim());
    setIsEditingTitle(false);
  };

  const submitArtistEdit = () => {
    setArtist((prev) => prev.trim());
    setIsEditingArtist(false);
  };

  const copy = locale === 'ja' ? {
    localMode: 'ローカルモード', unsaved: '未保存', saving: '保存中...', saved: '保存済み', saveError: '保存エラー', login: 'ログイン',
    newScore: '新しい曲', library: 'ライブラリ', libraryLoading: '読み込み中...',
    libraryEmpty: '保存済みの曲はありません', libraryError: 'ライブラリを読み込めませんでした', libraryLogin: 'ログインすると曲を表示できます',
    settings: '設定', proDescription: '無制限の曲と分析機能', removePart: 'パートを削除',
    keyChangeTitle: 'Keyを変更しますか？', keyChangeDescription: '入力済みのコードも新しいKeyに合わせて移調できます。',
    cancel: 'キャンセル', keyOnly: 'Keyのみ変更', transpose: 'コードも移調', titlePlaceholder: '曲名', artistPlaceholder: 'アーティスト',
  } : {
    localMode: 'Local mode', unsaved: 'Unsaved', saving: 'Saving...', saved: 'Saved', saveError: 'Save failed', login: 'Log in',
    newScore: 'New score', library: 'Library', libraryLoading: 'Loading...',
    libraryEmpty: 'No saved scores', libraryError: 'Could not load the library', libraryLogin: 'Log in to view your scores',
    settings: 'Settings', proDescription: 'Unlimited scores and analysis tools', removePart: 'Remove part',
    keyChangeTitle: 'Change key?', keyChangeDescription: 'You can also transpose entered chords to the new key.',
    cancel: 'Cancel', keyOnly: 'Change key only', transpose: 'Transpose chords', titlePlaceholder: 'Title', artistPlaceholder: 'Artist',
  };

  if (loading) {
    return (
      <div className="spinner-container spinner-container--screen" aria-label="Loading">
        <div className="spinner" />
      </div>
    );
  }

  if (isScoreNotFound) {
    return (
      <div className="page-shell">
        <Header user={user} showMenu />
        <div className="score-page-container page-main">
          <section className="score-not-found">
            <h1 className="score-not-found-title">Score not found</h1>
            <p className="score-not-found-text">
              The specified score does not exist or cannot be accessed.
            </p>
            <button
              type="button"
              className="score-not-found-cta"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Back to score list
            </button>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="degreebook-app">
      <header className="degreebook-topbar">
        <button className="degreebook-brand" type="button" onClick={() => { window.location.href = '/'; }}>
          <img src="/logo512.png" alt="Chord Analyzer" className="degreebook-brand-mark" />
          <span>Chord Analyzer</span>
          <span className="degreebook-plan-pill">{isGuestDraft ? 'GUEST' : (isPro ? 'PRO' : 'FREE')}</span>
        </button>
        <div className="degreebook-top-actions">
          <span className="degreebook-save-state">
            <i />
            {isGuestDraft
              ? copy.localMode
              : isDraftMode
                ? copy.unsaved
              : saveStatus === 'saving'
                ? copy.saving
                : saveStatus === 'error'
                  ? copy.saveError
                : `${copy.saved}${lastSavedAt ? ` (${lastSavedAt.toLocaleDateString(locale === 'ja' ? 'ja-JP' : 'en-US')} ${lastSavedAt.toLocaleTimeString(locale === 'ja' ? 'ja-JP' : 'en-US')})` : ''}`}
          </span>
          <button
            type="button"
            className="degreebook-lang"
            onClick={() => setLocalePreference(locale === 'ja' ? 'en' : 'ja')}
          >
            {locale === 'ja' ? 'JA / EN⌄' : 'EN / JA⌄'}
          </button>
          <SaveButton
            className="save-button--topbar"
            userId={userId}
            user={user}
            scoreId={activeScoreId}
            parts={parts}
            title={title}
            artist={artist}
            tempo={tempo}
            onScoreCreated={(createdScoreId) => {
              if (typeof window !== 'undefined') window.sessionStorage.removeItem(DRAFT_STORAGE_KEY);
              initialized.current = true;
              setIsDirty(false);
              setLastSavedAt(new Date());
              setSaveStatus('saved');
              setActiveScoreId(createdScoreId);
              window.history.replaceState({}, document.title, `/?scoreid=${createdScoreId}`);
            }}
          />
          {isGuestDraft ? <button type="button" className="degreebook-login" onClick={() => { window.location.href = '/login'; }}>{copy.login}</button> : (
            <img
              src={user?.picture || user?.photoURL || '/logo192.png'}
              alt="Google account"
              className="degreebook-avatar"
            />
          )}
        </div>
      </header>
      <div className="degreebook-layout">
        <aside className="degreebook-sidebar">
          <button className="degreebook-new-button" type="button" onClick={() => { window.location.href = '/?mode=compose'; }}>＋ {copy.newScore}</button>
          <nav className="degreebook-nav">
            <button
              type="button"
              className="active"
              onClick={() => setIsLibraryOpen((open) => !open)}
              aria-expanded={isLibraryOpen}
            >
              ▦ {copy.library} <span>{libraryStatus === 'ready' ? libraryScores.length : '—'}</span>
            </button>
            {isLibraryOpen && (
              <div className="degreebook-library-tree">
                {libraryArtists.map(([artistName, scores]) => (
                  <div className="degreebook-library-artist" key={artistName}>
                    <button
                      type="button"
                      onClick={() => setExpandedLibraryArtist((artist) => artist === artistName ? null : artistName)}
                      aria-expanded={expandedLibraryArtist === artistName}
                    >
                      <span>{expandedLibraryArtist === artistName ? '⌄' : '›'} {artistName}</span>
                      <small>{scores.length}</small>
                    </button>
                    {expandedLibraryArtist === artistName && scores.map((score) => (
                      <button
                        type="button"
                        className="degreebook-library-song"
                        key={score.scoreId}
                        onClick={() => { window.location.href = `/?scoreid=${encodeURIComponent(score.scoreId)}`; }}
                      >
                        {score.title || DEFAULT_SCORE_TITLE}
                      </button>
                    ))}
                  </div>
                ))}
                {libraryStatus === 'loading' && <span className="degreebook-library-empty">{copy.libraryLoading}</span>}
                {libraryStatus === 'error' && <span className="degreebook-library-empty degreebook-library-empty--error">{copy.libraryError}</span>}
                {libraryStatus === 'ready' && !libraryArtists.length && <span className="degreebook-library-empty">{copy.libraryEmpty}</span>}
                {libraryStatus === 'idle' && !userId && <span className="degreebook-library-empty">{copy.libraryLogin}</span>}
              </div>
            )}
          </nav>
          <div className="degreebook-sidebar-bottom">
            <button type="button" className="degreebook-nav-button">⚙ {copy.settings}</button>
            <button type="button" className="degreebook-pro-card" onClick={() => { window.location.href = '/upgrade'; }}><b>♛ Chord Analyzer PRO</b><small>{copy.proDescription}</small></button>
          </div>
        </aside>
        <main className="degreebook-main">
          <div className="score-page-container page-main">
        <section className="score-header score-meta-card">
          <div className="score-meta-primary">
            <div className="score-meta-icon" aria-hidden="true">♫</div>
            <div className="score-meta-copy">
          <>
            {isEditingTitle ? (
              <input
                className="score-meta-input score-meta-input--title"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  setIsDirty(true);
                }}
                onBlur={submitTitleEdit}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') submitTitleEdit();
                }}
                placeholder={copy.titlePlaceholder}
                autoFocus
              />
            ) : (
              <h1
                className="score-page-title score-page-title--editable"
                onDoubleClick={() => setIsEditingTitle(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setIsEditingTitle(true);
                  }
                }}
              >
                {title?.trim() || DEFAULT_SCORE_TITLE}
              </h1>
            )}
          </>
          {isEditingArtist ? (
            <input
              className="score-meta-input score-meta-input--artist"
              value={artist}
              onChange={(event) => {
                setArtist(event.target.value);
                setIsDirty(true);
              }}
              onBlur={submitArtistEdit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submitArtistEdit();
              }}
              placeholder={copy.artistPlaceholder}
              autoFocus
            />
          ) : (
            <div
              className="score-artist-label score-artist-label--editable"
              onDoubleClick={() => setIsEditingArtist(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setIsEditingArtist(true);
                }
              }}
            >
              {artist?.trim() || DEFAULT_SCORE_ARTIST}
            </div>
          )}
            </div>
          </div>
          <div className="score-meta-details">
            <div className="score-meta-control">
              <span className="score-meta-control-label">KEY</span>
              <button
                type="button"
                className="score-meta-key"
                onClick={() => setActiveKeyPartIndex(0)}
              >
                {parts[0]?.key || 'C'}
              </button>
            </div>
            <BPMInput value={tempo} onChange={handleTempoChange} compact />
            <div className="score-meta-control">
              <span className="score-meta-control-label">TIME SIG.</span>
              <span className="score-meta-static">4 / 4</span>
            </div>
          </div>
        </section>
        <section className={`harmony-tools-sticky${isHarmonyToolsVisible ? '' : ' is-collapsed'}`} aria-label="Harmony tools">
          <button
            type="button"
            className="harmony-tools-toggle"
            onClick={() => setIsHarmonyToolsVisible((visible) => !visible)}
            aria-expanded={isHarmonyToolsVisible}
            aria-label={isHarmonyToolsVisible ? 'Hide harmony tools' : 'Show harmony tools'}
          >
            {isHarmonyToolsVisible ? '▼' : '▲'}
          </button>
          {isHarmonyToolsVisible && (
            <div className="harmony-tools-inner">
              <CircleOfFifths
                selectedKey={harmonyPreviewKey || parts[0]?.key || 'C'}
                baseKey={parts[0]?.key || 'C'}
                onSelect={setHarmonyPreviewKey}
              />
              <ChordPalette onApply={applyPaletteChord} selectedKey={harmonyPreviewKey || parts[0]?.key || 'C'} locale={locale} />
            </div>
          )}
        </section>
        {parts.map((part, idx) => (
          <div key={idx} style={{ marginBottom: '1.25rem' }}>
            {parts.length > 1 && (
              <div className="score-part-actions">
                <button
                  type="button"
                  className="remove-key-button"
                  onClick={() => removePart(idx)}
                >
                  {copy.removePart}
                </button>
              </div>
            )}
            <ScoreSheet
              partIndex={idx}
              selectedKey={part.key}
              rows={part.rows || []}
              onRowsChange={(newRows) => handleRowsChange(idx, newRows)}
              onRowKeySelect={(rowIndex) => {
                setHarmonyPreviewKey(part.rows[rowIndex]?.key || part.key);
                setActiveRowKeyTarget({ partIndex: idx, rowIndex });
              }}
              onRowKeyClear={(rowIndex) => requestRowKeyReset(idx, rowIndex)}
              onActiveInputCellChange={handleActiveInputCellChange}
              showSequenceControl={idx === 0}
              isSequencePlaying={isSequencePlaying}
              onToggleSequence={toggleSequence}
              sequenceProgress={sequenceProgress}
              tempo={tempo}
              activeCell={
                activeCell?.partIndex === idx
                  ? { rowIndex: activeCell.rowIndex, colIndex: activeCell.colIndex }
                  : null
              }
            />
          </div>
        ))}

        {/* Key 選択モーダル */}
        <KeySelectModal
          isOpen={activeKeyPartIndex !== null || activeRowKeyTarget !== null}
          currentKey={
            activeRowKeyTarget !== null
              ? parts[activeRowKeyTarget.partIndex]?.rows?.[activeRowKeyTarget.rowIndex]?.key || parts[activeRowKeyTarget.partIndex]?.key
              : activeKeyPartIndex !== null ? parts[activeKeyPartIndex].key : undefined
          }
          onClose={() => {
            setActiveKeyPartIndex(null);
            setActiveRowKeyTarget(null);
          }}
          onSelect={(newKey) => {
            if (activeRowKeyTarget !== null) {
              const { partIndex, rowIndex } = activeRowKeyTarget;
              const row = parts[partIndex]?.rows?.[rowIndex];
              const change = { type: 'row', partIndex, rowIndex, fromKey: row?.key || parts[partIndex]?.key, toKey: newKey };
              if (row?.chords?.some((chord) => chord?.trim())) setPendingKeyChange(change);
              else applyKeyChange(change, false);
              setActiveRowKeyTarget(null);
            } else if (activeKeyPartIndex !== null) {
              const change = { type: 'base', fromKey: parts[0]?.key || 'C', toKey: newKey };
              const hasChords = parts.some((part) => part.rows?.some((row) => !row.key && row.chords?.some((chord) => chord?.trim())));
              if (hasChords) setPendingKeyChange(change);
              else handleBaseKeyChange(newKey);
            }
          }}
        />
        <Modal isOpen={pendingKeyChange !== null} onClose={() => setPendingKeyChange(null)} contentClassName="modal-content--modern">
          <div className="modal-header">
            <h2 className="modal-title">{copy.keyChangeTitle}</h2>
            <p className="modal-subtitle">{copy.keyChangeDescription}</p>
          </div>
          <div className="modal-buttons">
            <button type="button" className="cancel" onClick={() => setPendingKeyChange(null)}>{copy.cancel}</button>
            <button type="button" className="cancel" onClick={() => applyKeyChange(pendingKeyChange, false)}>{copy.keyOnly}</button>
            <button type="button" className="create" onClick={() => applyKeyChange(pendingKeyChange, true)}>{copy.transpose}</button>
          </div>
        </Modal>

        <div style={{ textAlign: 'center', marginBottom: '1.15rem' }}>
          <AddPartButton onClick={addPart} />
        </div>
        {canShowAds && (
          <section className="score-ad-slot" aria-label="Advertisement">
            <AdMaxUnit />
          </section>
        )}
      </div>
        </main>
      </div>
    </div>
  );
}
